import { Box, Flex, Spinner } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { FaExpand } from 'react-icons/fa6';
import { GrDownload } from 'react-icons/gr';
import { HiOutlineMenuAlt4 } from 'react-icons/hi';
import { MdAdd } from 'react-icons/md';
import { RiCloseLine, RiFullscreenExitLine } from 'react-icons/ri';
import { RxHamburgerMenu } from 'react-icons/rx';
import HorizontalBarsIcon from '@/assets/icons/HorizontalBarsIcon';
import VerticalBarsIcon from '@/assets/icons/VerticalBarsIcon';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import ActionMenuWithTutorialSupport from '@/components/Molecules/ActionMenuWithTutorialSupport';
import Show from '@/components/Molecules/Show';
import type { DataTableFiltersProps } from '@/components/Organisms/DataTable/DataTable.types';
import DataTableColumnFilterMenu from '@/components/Organisms/DataTable/DataTableColumnFilterMenu';
import DataTableExportFilterMenu from '@/components/Organisms/DataTable/DataTableExportFilterMenu';
import { getForceOpenCreateMenuForTutorial } from '@/hooks/tutorialsHooks/useRequestsTutorial';
import { useTutorialActive } from '@/hooks/tutorialsHooks/useTutorialActive';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import { useHoverMenu } from '@/hooks/useHoverMenu';
import { useTourContext } from '@/store/tourContext';
import { FiltersDrawer } from '../SidebarDrawer';

const DataTableFilters = ({
	searchable = false,
	hideShowColumn = false,
	expandable = false,
	fullscreen = false,
	exportable = false,
	selectedRows = [],
	globalFilter,
	creatableFn,
	statusFilters = [],
	globalActions = [],
	status,
	setStatus,
	size,
	setSize,
	search,
	setSearch,
	columns = [],
	hiddenColumn = [],
	setHiddenColumn,
	addButtonRef,
	exportButtonRef,
	csv,
	excel,
	pdf,
	exportAllAsCSV,
	exportAllAsExcel,
	exportAllAsPDF,
	exportCurrentAsCSV,
	exportCurrentAsExcel,
	exportCurrentAsPDF,
	defaultGlobalFilters,
	onChangeGlobalFilter,
	globalDateFilters,
	globalDateFilter,
	setGlobalDateFilter,
	isExportingAll,
}: DataTableFiltersProps) => {
	const [showSearchInput, setShowSearchInput] = useState(false);
	const [searchInput, setSearchInput] = useState<string>(search ?? '');
	const [fullScreenMode, setFullScreenMode] = useState(false);

	// Hover menu state for create button
	const [createMenuOpen, setCreateMenuOpen] = useState(false);
	const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
	const createButtonRef = useRef<HTMLDivElement>(null);
	const dropdownLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Long delay only when leaving button (time to reach dropdown). Dropdown leave uses a short separate debounce.
	const { cancelClose, scheduleClose } = useHoverMenu(1500);

	useEffect(() => {
		return () => {
			if (dropdownLeaveTimerRef.current) clearTimeout(dropdownLeaveTimerRef.current);
		};
	}, []);

	// Determine if creatableFn is a function or array
	const isCreateMenuArray = Array.isArray(creatableFn);
	const createMenuItems = isCreateMenuArray ? creatableFn : [];

	// Shared clinic filter helpers (key used by backend for clinic filter)
	const { clinicFilterItems } = useClinicFilter('_ta_clinic_value');

	// Check if any tutorial is active (for showing actions button during any tutorial)
	const { isTourActive } = useTourContext();
	const { isRequestsTutorialActive } = useTutorialActive();

	const updateSize = () => {
		if (size === 2) {
			setSize(4);
		} else if (size === 4) {
			setSize(6);
		} else {
			setSize(2);
		}
	};

	// When tutorial is active, sync create menu open state with tutorial actions (OpenMenuOrders / closeMenuOrders).
	// Force-open when the tutorial asks for it; force-close when closeMenuOrders runs so the menu actually closes.
	useEffect(() => {
		if (!isCreateMenuArray || !isRequestsTutorialActive) return;

		const updateMenuState = () => {
			if (!createButtonRef.current) return;
			if (getForceOpenCreateMenuForTutorial()) {
				const rect = createButtonRef.current.getBoundingClientRect();
				setMenuPosition({
					x: rect.left,
					y: rect.bottom + 1,
				});
				cancelClose();
				setCreateMenuOpen(true);
			} else {
				setCreateMenuOpen(false);
			}
		};

		updateMenuState();
		const interval = setInterval(updateMenuState, 200);
		return () => clearInterval(interval);
	}, [isCreateMenuArray, isRequestsTutorialActive, cancelClose]);

	// Setup hover effect for create button when it's a menu
	useEffect(() => {
		if (!isCreateMenuArray || !createButtonRef.current) return;
		// Don't set up hover listeners if tutorial is forcing the menu open
		if (isRequestsTutorialActive && getForceOpenCreateMenuForTutorial()) return;

		const buttonContainer = createButtonRef.current;

		const handleMouseEnter = () => {
			if (dropdownLeaveTimerRef.current) {
				clearTimeout(dropdownLeaveTimerRef.current);
				dropdownLeaveTimerRef.current = null;
			}
			const rect = buttonContainer.getBoundingClientRect();
			// Use 1px gap so cursor doesn’t leave hover area when moving to dropdown
			setMenuPosition({
				x: rect.left,
				y: rect.bottom + 1,
			});
			cancelClose();
			setCreateMenuOpen(true);
		};

		const handleMouseLeave = () => {
			scheduleClose(() => setCreateMenuOpen(false));
		};

		buttonContainer.addEventListener('mouseenter', handleMouseEnter);
		buttonContainer.addEventListener('mouseleave', handleMouseLeave);

		return () => {
			buttonContainer.removeEventListener('mouseenter', handleMouseEnter);
			buttonContainer.removeEventListener('mouseleave', handleMouseLeave);
		};
	}, [isCreateMenuArray, cancelClose, scheduleClose, isRequestsTutorialActive]);

	const toggleTableFullScreen = () => {
		const tableEl = document.querySelector(
			'[data-id="smart-data-table"]',
		) as HTMLElement | null;
		if (!tableEl) return;

		const isCurrentlyFullscreen = tableEl.classList.contains('fullscreen-mode');

		if (!isCurrentlyFullscreen) {
			tableEl.classList.add('fullscreen-mode');
			document.body.style.overflow = 'hidden';
			setFullScreenMode(true);
		} else {
			tableEl.classList.remove('fullscreen-mode');
			document.body.style.overflow = '';
			setFullScreenMode(false);
		}
	};

	return (
		<Box>
			<Flex
				flexWrap="wrap"
				justify="flex-end"
				align="center"
				gap={3}
				width="100%"
			>
				<Show when={isExportingAll || false}>
					<Flex
						align="center"
						gap={2}
						color="brand.600"
						fontSize="sm"
						className="export-indicator"
					>
						<Box className="export-indicator-text">Exporting data...</Box>
						<Spinner zIndex={2} size="sm" color="brand.600" />
					</Flex>
				</Show>
				<Show when={searchable}>
					<Box
						transition="all 0.5s ease-in-out"
						display="flex"
						width={showSearchInput ? '198px' : '36px'}
					>
						<Show when={!showSearchInput}>
							<FaceliftIconButton
								cursor="pointer"
								bg="transparent"
								color="brand.inputText"
								transition="all 0.5s ease-in-out"
								onClick={() => setShowSearchInput(!showSearchInput)}
								title="Search"
							>
								<AiOutlineSearch />
							</FaceliftIconButton>
						</Show>
						<Show when={showSearchInput}>
							<Box
								transition="all 0.5s ease-in-out"
								display="flex"
								width={showSearchInput ? '198px' : '0px'}
							>
								<FaceliftInput
									bg="transparent"
									placeholder="Type and press Enter"
									autoFocus={true}
									bordered={true}
									value={searchInput}
									onChange={(e) => {
										const query = e.target.value;
										setSearchInput(query);
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											setSearch(searchInput.trim());
										}
									}}
									startIcon={
										<Box
											cursor="pointer"
											color="brand.inputText"
											title="Search"
										>
											<AiOutlineSearch />
										</Box>
									}
									endIcon={
										<Box
											cursor="pointer"
											onClick={() => {
												setShowSearchInput(!showSearchInput);
												setSearch('');
												setSearchInput('');
											}}
											color="brand.inputText"
											title="Close search"
										>
											<RiCloseLine />
										</Box>
									}
								/>
							</Box>
						</Show>
					</Box>
				</Show>
				<Show when={hideShowColumn}>
					<Box
						bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
						zIndex={2}
					>
						<DataTableColumnFilterMenu
							columns={columns}
							hiddenColumn={hiddenColumn}
							setHiddenColumn={setHiddenColumn}
							trigger={
								<FaceliftIconButton bg="transparent" color="brand.inputText">
									<VerticalBarsIcon aria-label="Show/hide columns" />
								</FaceliftIconButton>
							}
						/>
					</Box>
				</Show>
				<Show when={expandable}>
					<FaceliftIconButton
						bg="transparent"
						color="brand.inputText"
						onClick={updateSize}
						title="Table density"
					>
						<Show when={size === 2}>
							<HorizontalBarsIcon aria-label="Table density" />
						</Show>
						<Show when={size === 4}>
							<RxHamburgerMenu />
						</Show>
						<Show when={size === 6}>
							<HiOutlineMenuAlt4 />
						</Show>
					</FaceliftIconButton>
				</Show>
				<Show when={fullscreen}>
					<FaceliftIconButton
						bg="transparent"
						color="brand.inputText"
						onClick={toggleTableFullScreen}
						title={fullScreenMode ? 'Exit' : 'Full screen'}
					>
						<Show when={!fullScreenMode}>
							<FaExpand />
						</Show>
						<Show when={fullScreenMode}>
							<RiFullscreenExitLine />
						</Show>
					</FaceliftIconButton>
				</Show>

				<Show
					when={
						!!globalActions &&
						globalActions.length > 0 &&
						(selectedRows.length > 0 || isTourActive)
					}
				>
					<Box width="120px">
						<ActionMenuWithTutorialSupport
							actionItems={globalActions}
							selectedRows={selectedRows}
						/>
					</Box>
				</Show>

				{/* Simple button when creatableFn is a function */}
				<Show when={!!creatableFn && typeof creatableFn === 'function'}>
					<FaceliftIconButton
						ref={addButtonRef}
						onClick={creatableFn as () => void}
						title="Add"
					>
						<MdAdd />
					</FaceliftIconButton>
				</Show>

				{/* Button with hover menu when creatableFn is an array */}
				<Show when={isCreateMenuArray && createMenuItems.length > 0}>
					<Box position="relative">
						<Box ref={createButtonRef}>
							<FaceliftIconButton ref={addButtonRef} title="Add">
								<MdAdd />
							</FaceliftIconButton>
						</Box>

						{/* Hover Menu - small top padding so cursor stays in hover zone when moving from button */}
						<Show when={createMenuOpen}>
							<Box
								position="fixed"
								left={`${menuPosition.x}px`}
								top={`${menuPosition.y}px`}
								pt="1"
								mt="-1px"
								bg="white"
								_dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
								border="1px solid"
								borderColor="gray.200"
								boxShadow="lg"
								borderRadius="md"
								minW="180px"
								overflow="hidden"
								zIndex={1500}
								onMouseEnter={() => {
									if (dropdownLeaveTimerRef.current) {
										clearTimeout(dropdownLeaveTimerRef.current);
										dropdownLeaveTimerRef.current = null;
									}
									cancelClose();
								}}
								onMouseLeave={() => {
									if (dropdownLeaveTimerRef.current) clearTimeout(dropdownLeaveTimerRef.current);
									dropdownLeaveTimerRef.current = setTimeout(() => {
										dropdownLeaveTimerRef.current = null;
										setCreateMenuOpen(false);
									}, 100);
								}}
							>
								{createMenuItems.map((item, index) => (
									<Box
										key={`index-${item.label}-${index}`}
										px="4"
										py="2"
										display="flex"
										alignItems="center"
										_hover={{
											background: 'brand.solidGradient',
											color: 'white',
										}}
										cursor="pointer"
										onClick={() => {
											item.action();
											setCreateMenuOpen(false);
										}}
										fontSize="sm"
										fontWeight={500}
									>
										{item.label}
									</Box>
								))}
							</Box>
						</Show>
					</Box>
				</Show>

				<Show when={exportable}>
					<DataTableExportFilterMenu
						trigger={
							<FaceliftIconButton
								ref={exportButtonRef}
								bg="transparent"
								color="brand.inputText"
								borderColor="brand.inputText"
								title="Export"
								_hover={{
									bg: 'brand.solidGradient',
									color: 'white',
									border: 'none',
								}}
							>
								<GrDownload />
							</FaceliftIconButton>
						}
						csv={csv}
						excel={excel}
						pdf={pdf}
						exportAllAsCSV={exportAllAsCSV}
						exportAllAsExcel={exportAllAsExcel}
						exportAllAsPDF={exportAllAsPDF}
						exportCurrentAsCSV={exportCurrentAsCSV}
						exportCurrentAsExcel={exportCurrentAsExcel}
						exportCurrentAsPDF={exportCurrentAsPDF}
					/>
				</Show>
				<Show when={!!globalDateFilters.length}>
					<Box width="150px">
						<FaceliftSelect
							items={globalDateFilters}
							isSearchable={false}
							value={globalDateFilter}
							onChange={(newValue) => {
								const selectedValue = newValue as SelectItem;
								// setGlobalDateFilter will call handleGlobalDateFilterChange which opens modal for 'custom'
								setGlobalDateFilter(selectedValue);
							}}
						/>
					</Box>
				</Show>
				<Show when={!!statusFilters.length}>
					<Box width="150px">
						<FaceliftSelect
							items={statusFilters}
							isSearchable={false}
							value={status}
							onChange={(newValue) => setStatus(newValue as SelectItem)}
						/>
					</Box>
				</Show>
				<Show when={!!globalFilter}>
					<FiltersDrawer
						title={globalFilter?.title || 'Filter'}
						onChangeFilter={onChangeGlobalFilter}
						defaultValues={defaultGlobalFilters}
						showSearch={globalFilter?.clinics}
						items={[
							...(globalFilter?.clinics ? clinicFilterItems : []),
							...(globalFilter?.items || []),
						]}
					/>
				</Show>
			</Flex>
		</Box>
	);
};

export default DataTableFilters;
